import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { fetchCodes, addCode, updateCode, deleteCode, fetchClients, fetchMails } from '../api/github';
import SearchIcon from '@mui/icons-material/Search';
import FloatingActionButton from './FloatingActionButton';
import LoadingAnimation from './LoadingAnimation';

const formatCurrency = (amount) => {
  return amount ? `${amount.toLocaleString()} Сум` : '0 Сум';
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'Йозилмаган';
  const date = new Date(dateString);
  return date.toLocaleString();
};

export default function CodesManager() {
  const [codes, setCodes] = useState([]);
  const [clients, setClients] = useState([]);
  const [mails, setMails] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCode, setCurrentCode] = useState(null);
  const [codeValue, setCodeValue] = useState('');
  const [codeDescription, setCodeDescription] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('value');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const [errors, setErrors] = useState({
    value: false,
    description: false
  });

  const validateForm = () => {
    const newErrors = {
      value: !codeValue.trim(),
      description: !codeDescription.trim()
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [codesData, clientsData, mailsData] = await Promise.all([
        fetchCodes(),
        fetchClients(),
        fetchMails()
      ]);
      setCodes(codesData);
      setClients(clientsData);
      setMails(mailsData);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      alert(error.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (code = null) => {
    setCurrentCode(code);
    setCodeValue(code?.value || '');
    setCodeDescription(code?.description || '');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCode(null);
    setCodeValue('');
    setCodeDescription('');
    loadData();
  };

  const filteredCodes = codes
    .filter(code => {
      const matchesSearch =
        filterField === 'value' && code.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        filterField === 'description' && code.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'unused' && !code.isUsed) ||
        (statusFilter === 'used' && code.isUsed);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => a.isUsed - b.isUsed); // Неиспользованные (false) первыми

  const handleSubmit = async () => {
    try {
      if (!validateForm()) return;
      const allCodes = await fetchCodes(true);

      const codeExists = allCodes.some(c =>
        c.value === codeValue && c.id !== (currentCode?.id || null)
      );

      if (codeExists) {
        throw new Error('Шунака код уже бор!');
      }

      const codeData = {
        value: codeValue,
        description: codeDescription
      };

      if (currentCode) {
        await updateCode(currentCode.id, codeData);
      } else {
        await addCode(codeData);
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error("Ошибка при сохранении кода:", error);
      alert(error.message);
    }
  };

  const handleDelete = async (id) => {
    const codeToDelete = codes.find(c => c.id === id);

    if (window.confirm('Кодни ўчирмоқчимисиз?')) {
      try {
        await deleteCode(id);
        loadData();
      } catch (error) {
        console.error("Ошибка при удалении кода:", error);
        alert(error.message);
      }
    }
  };

  const getCodeUsageDetails = (codeValue) => {
    // Ищем клиента с этим кодом
    const client = clients.find(c => c.code === codeValue);
    if (client) {
      const order = client.orders?.[0] || {};
      return {
        type: 'client',
        nickname: client.nickname,
        date: order.date,
        totalAmount: order.totalAmount,
        products: order.products || [],
        address: order.deliveryInfo?.address,
        partyComment: order.partyComment,
        review: order.review
      };
    }

    // Ищем почту с этим кодом
    const mail = mails.find(m => m.code === codeValue);
    if (mail) {
      const totalAmount = mail.products?.reduce((sum, item) => sum + (item.productPrice * item.quantity), 0) || 0;
      return {
        type: 'mail',
        nickname: mail.nickname,
        date: mail.createdAt,
        totalAmount,
        products: mail.products || [],
        address: mail.address,
        partyComment: mail.partyComment,
        age: mail.age
      };
    }

    return null;
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 1 }}>
      <Divider sx={{ mb: 10 }} />
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        mb: 0,
        gap: 0
      }}>
        <FloatingActionButton
          onClick={() => handleOpenDialog()}
          tooltip="Код қўшиш"
        />
      </Box>
      <Box sx={{ mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
        <TextField
          placeholder="Код қидириш..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          fullWidth
        />
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Ҳолати</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Ҳолати"
          >
            <MenuItem value="all">Ҳаммаси</MenuItem>
            <MenuItem value="used">Ишлатилган</MenuItem>
            <MenuItem value="unused">Ишлатилмаган</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <List sx={{ bgcolor: 'background.paper' }}>
        {loading ? (
          <LoadingAnimation loading={loading} />
        ) : codes.length === 0 ? (
          <Typography variant="body1" sx={{ p: 2 }}>
            Кодлар топилмади
          </Typography>
        ) : (
          filteredCodes.map(code => {
            if (!code.isUsed) {
              return (
                <ListItem
                  key={code.id}
                  secondaryAction={
                    <Box>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => handleOpenDialog(code)}
                        sx={{ mr: 0 }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                          label={code.value}
                          sx={{
                            color: "white",
                            background: "green",
                          }}
                          variant="filled"
                        />
                        <Typography variant="body1">
                          {code.description}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2">
                        Яратилди: {formatDateTime(code.createdAt)}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            } else {
              const usageDetails = getCodeUsageDetails(code.value);
              return (
                <Accordion key={code.id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip
                            label={code.value}
                            sx={{
                              color: "white",
                              background: "red",
                            }}
                            variant="filled"
                          />
                          <Typography variant="body1">
                            {code.description}
                            <Typography component="span" variant="body2" color="text.secondary">
                              (ишлатилди)
                            </Typography>
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2">
                          Ишлатилди: {formatDateTime(code.usedAt)}
                        </Typography>
                      }
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 0 }}>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDialog(code);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {usageDetails ? (
                      <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          {usageDetails.type === 'client' ? 'Клиент маълумотлари' : 'Почта маълумотлари'}
                        </Typography>
                        <Typography variant="body2">
                          Ник: {usageDetails.nickname}
                        </Typography>
                        <Typography variant="body2">
                          Сана: {formatDateTime(usageDetails.date)}
                        </Typography>
                        <Typography variant="body2">
                          Сумма: {formatCurrency(usageDetails.totalAmount)}
                        </Typography>
                        {usageDetails.address && (
                          <Typography variant="body2">
                            Манзил: {usageDetails.address}
                          </Typography>
                        )}
                        {usageDetails.age && (
                          <Typography variant="body2">
                            Ёш: {usageDetails.age}
                          </Typography>
                        )}
                        {usageDetails.partyComment && (
                          <Typography variant="body2">
                            Тўй маълумотлари: {usageDetails.partyComment}
                          </Typography>
                        )}
                        {usageDetails.review && (
                          <Typography variant="body2">
                            Баҳо: {usageDetails.review}
                          </Typography>
                        )}
                        {usageDetails.products?.length > 0 && (
                          <>
                            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                              Махсулотлар
                            </Typography>
                            <List dense>
                              {usageDetails.products.map((item, idx) => (
                                <ListItem key={idx}>
                                  <ListItemAvatar>
                                    <Avatar
                                      src={item.productImage}
                                      alt={item.productName}
                                      variant="square"
                                      sx={{ width: 40, height: 40, mr: 2 }}
                                    />
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={`${item.productName || 'Махсулот'} x${item.quantity}`}
                                    secondary={
                                      <Typography component="span" display="block">
                                        {formatCurrency(item.productPrice * item.quantity)}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ p: 2 }}>
                        Маълумотлар топилмади
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            }
          })
        )}
      </List>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {currentCode ? 'Кодни тахрирлаш' : 'Янги код кўшиш'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Код"
              fullWidth
              value={codeValue}
              onChange={(e) => setCodeValue(e.target.value)}
              margin="normal"
              error={errors.value}
              required
            />
            <TextField
              label="Код ники"
              fullWidth
              multiline
              rows={3}
              error={errors.description}
              value={codeDescription}
              onChange={(e) => setCodeDescription(e.target.value)}
              margin="normal"
            />
          </Box>

          {
            currentCode ? 
            <IconButton 
              onClick={() => {handleDelete(currentCode.id); handleCloseDialog(); } }
              color="error"
              sx={{ mt: isMobile ? 0 : 1, border: '1px solid red', borderRadius: '3px', width: '100%' }}
              fullWidth
            >
              <DeleteIcon fontSize="small" />
            </IconButton> : <DialogTitle></DialogTitle>
          }

        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Бекор қилиш</Button>
          <Button onClick={handleSubmit} variant="contained">
            {currentCode ? 'Сақлаш' : 'Қўшиш'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}